export interface ApplicationDocument {
  id: string | number;
  originalName?: string;
  fileName?: string;
  createdAt?: string;
}

export function normalizeDocumentMetadata(value: unknown): string {
  // Preserve malformed strings verbatim: a later save must not erase saved metadata.
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && !Array.isArray(value)) return JSON.stringify(value);
  return value == null ? '' : JSON.stringify(value);
}

export function parseDocumentMetadata(value: unknown): Record<string, any> {
  const parsed = typeof value === 'string' ? (value.trim() ? JSON.parse(value) : {}) : value;
  if (parsed == null) return {};
  if (typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Invalid document metadata');
  return parsed;
}

export function resolveApplicationDocuments(value: unknown, files: ApplicationDocument[]) {
  let metadata: Record<string, any> = {};
  let metadataError = false;
  try { metadata = parseDocumentMetadata(value); } catch { metadataError = true; }
  const matched: Record<string, ApplicationDocument> = {};
  const matchedIds = new Set<string>();
  for (const [type, entry] of Object.entries(metadata)) {
    if (!['convictionCheck', 'passport', 'transcripts', 'certificates', 'workHistory', 'ieltsToefl', 'visa', 'employmentContract'].includes(type) || !entry || entry.id == null) continue;
    const file = files.find(file => String(file.id) === String(entry.id));
    if (file) {
      matched[type] = { ...file, fileName: file.originalName || file.fileName || entry.fileName || `Document ${file.id}` };
      matchedIds.add(String(file.id));
    }
  }
  return { matched, unmatched: files.filter(file => !matchedIds.has(String(file.id))), metadataError };
}

/** A metadata refresh must not discard a user's selected file or an active upload. */
export function mergeDocumentSelection<T extends { file: unknown; uploading: boolean }>(previous: T, loaded: T, applicationChanged: boolean): T {
  return !applicationChanged && (previous.file || previous.uploading) ? previous : loaded;
}
